import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { apiService } from "../../backend/api";
import { getBaseUrl } from "../../config/apiConfig";
import { Rocket, Gift, CreditCard, Download, Save, Loader2, ToggleLeft, ToggleRight, Calendar, Percent, Clock, AlertCircle } from "lucide-react";

const AdminLaunchPromotions = () => {
  const { t } = useTranslation();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const data = await apiService.request(`${getBaseUrl()}/api/admin/launch-promotions`);
      setSettings(data);
    } catch (error) {
      console.error("Error fetching launch promotions:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setSuccess(false);
    try {
      const data = await apiService.request(`${getBaseUrl()}/api/admin/launch-promotions`, {
        method: "PUT",
        body: JSON.stringify({
          subscriptionDiscount: settings.subscriptionDiscount,
          packageDiscount: settings.packageDiscount,
          registrationBonus: settings.registrationBonus,
        }),
      });
      setSettings(data);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (error) {
      console.error("Error saving launch promotions:", error);
    } finally {
      setSaving(false);
    }
  };

  const updateField = (section, field, value) => {
    setSettings(prev => ({
      ...prev,
      [section]: { ...prev[section], [field]: value }
    }));
  };

  const formatDate = (date) => {
    if (!date) return "";
    return new Date(date).toISOString().split("T")[0];
  };

  const isExpired = (endDate) => {
    if (!endDate) return false;
    return new Date(endDate) < new Date();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="animate-spin text-primary" size={32} />
      </div>
    );
  }

  if (!settings) return null;

  const sections = [
    {
      key: "subscriptionDiscount",
      title: "Abo-Rabatt",
      subtitle: "Automatischer Rabatt auf neue Abonnements",
      icon: CreditCard,
      color: "blue",
      fields: (
        <>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 mb-1.5">Rabatt (%)</label>
              <div className="relative">
                <input
                  type="number"
                  min="1"
                  max="100"
                  value={settings.subscriptionDiscount.percent}
                  onChange={(e) => updateField("subscriptionDiscount", "percent", Number(e.target.value))}
                  className="w-full px-3 py-2.5 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl text-sm font-semibold focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none"
                />
                <Percent size={14} className="absolute right-3 top-3 text-slate-400" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 mb-1.5">Dauer (Monate)</label>
              <div className="relative">
                <input
                  type="number"
                  min="1"
                  max="24"
                  value={settings.subscriptionDiscount.durationMonths}
                  onChange={(e) => updateField("subscriptionDiscount", "durationMonths", Number(e.target.value))}
                  className="w-full px-3 py-2.5 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl text-sm font-semibold focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none"
                />
                <Clock size={14} className="absolute right-3 top-3 text-slate-400" />
              </div>
            </div>
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 mb-1.5">Ablaufdatum (optional)</label>
            <div className="relative">
              <input
                type="date"
                value={formatDate(settings.subscriptionDiscount.endDate)}
                onChange={(e) => updateField("subscriptionDiscount", "endDate", e.target.value || null)}
                className="w-full px-3 py-2.5 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl text-sm font-semibold focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none"
              />
              <Calendar size={14} className="absolute right-3 top-3 text-slate-400" />
            </div>
          </div>
          {settings.subscriptionDiscount.enabled && (
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800/30 rounded-xl p-3 text-xs text-blue-700 dark:text-blue-300">
              Wird automatisch beim Checkout angewendet — {settings.subscriptionDiscount.percent}% Rabatt für die ersten {settings.subscriptionDiscount.durationMonths} Monate.
              {settings.subscriptionDiscount.stripeCouponId && (
                <span className="block mt-1 text-[10px] opacity-60">Stripe Coupon: {settings.subscriptionDiscount.stripeCouponId}</span>
              )}
            </div>
          )}
        </>
      ),
    },
    {
      key: "packageDiscount",
      title: "Download-Paket-Rabatt",
      subtitle: "Automatischer Rabatt auf Einmalkauf-Pakete",
      icon: Download,
      color: "emerald",
      fields: (
        <>
          <div>
            <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 mb-1.5">Rabatt (%)</label>
            <div className="relative">
              <input
                type="number"
                min="1"
                max="100"
                value={settings.packageDiscount.percent}
                onChange={(e) => updateField("packageDiscount", "percent", Number(e.target.value))}
                className="w-full px-3 py-2.5 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl text-sm font-semibold focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 outline-none"
              />
              <Percent size={14} className="absolute right-3 top-3 text-slate-400" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 mb-1.5">Ablaufdatum (optional)</label>
            <div className="relative">
              <input
                type="date"
                value={formatDate(settings.packageDiscount.endDate)}
                onChange={(e) => updateField("packageDiscount", "endDate", e.target.value || null)}
                className="w-full px-3 py-2.5 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl text-sm font-semibold focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 outline-none"
              />
              <Calendar size={14} className="absolute right-3 top-3 text-slate-400" />
            </div>
          </div>
          {settings.packageDiscount.enabled && (
            <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-100 dark:border-emerald-800/30 rounded-xl p-3 text-xs text-emerald-700 dark:text-emerald-300">
              Wird automatisch beim Checkout angewendet — {settings.packageDiscount.percent}% Rabatt auf alle Download-Pakete.
              {settings.packageDiscount.stripeCouponId && (
                <span className="block mt-1 text-[10px] opacity-60">Stripe Coupon: {settings.packageDiscount.stripeCouponId}</span>
              )}
            </div>
          )}
        </>
      ),
    },
    {
      key: "registrationBonus",
      title: "Registrierungs-Bonus",
      subtitle: "Kostenlose Downloads bei Neuregistrierung",
      icon: Gift,
      color: "amber",
      fields: (
        <>
          <div>
            <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 mb-1.5">Bonus-Downloads</label>
            <div className="relative">
              <input
                type="number"
                min="1"
                max="100"
                value={settings.registrationBonus.downloads}
                onChange={(e) => updateField("registrationBonus", "downloads", Number(e.target.value))}
                className="w-full px-3 py-2.5 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl text-sm font-semibold focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 outline-none"
              />
              <Download size={14} className="absolute right-3 top-3 text-slate-400" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 mb-1.5">Ablaufdatum (optional)</label>
            <div className="relative">
              <input
                type="date"
                value={formatDate(settings.registrationBonus.endDate)}
                onChange={(e) => updateField("registrationBonus", "endDate", e.target.value || null)}
                className="w-full px-3 py-2.5 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl text-sm font-semibold focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 outline-none"
              />
              <Calendar size={14} className="absolute right-3 top-3 text-slate-400" />
            </div>
          </div>
          {settings.registrationBonus.enabled && (
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-100 dark:border-amber-800/30 rounded-xl p-3 text-xs text-amber-700 dark:text-amber-300">
              Jeder neue Nutzer erhält {settings.registrationBonus.downloads} Bonus-Downloads bei Registrierung.
            </div>
          )}
        </>
      ),
    },
  ];

  const colorMap = {
    blue: { bg: "bg-blue-50 dark:bg-blue-900/20", border: "border-blue-100 dark:border-blue-800/30", icon: "text-blue-600 dark:text-blue-400", toggle: "bg-blue-500" },
    emerald: { bg: "bg-emerald-50 dark:bg-emerald-900/20", border: "border-emerald-100 dark:border-emerald-800/30", icon: "text-emerald-600 dark:text-emerald-400", toggle: "bg-emerald-500" },
    amber: { bg: "bg-amber-50 dark:bg-amber-900/20", border: "border-amber-100 dark:border-amber-800/30", icon: "text-amber-600 dark:text-amber-400", toggle: "bg-amber-500" },
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
            <Rocket size={20} className="text-white" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-slate-800 dark:text-white">Launch-Aktionen</h2>
            <p className="text-xs text-slate-500 dark:text-slate-400">Automatische Rabatte & Boni für den MVP-Start</p>
          </div>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-bold text-sm transition-all ${
            success
              ? "bg-green-500 text-white"
              : "bg-slate-800 dark:bg-slate-600 text-white hover:bg-slate-900 dark:hover:bg-slate-500"
          }`}
        >
          {saving ? <Loader2 size={16} className="animate-spin" /> : success ? "✓ Gespeichert" : <><Save size={16} /> Speichern</>}
        </button>
      </div>

      {/* Promotion Sections */}
      {sections.map(({ key, title, subtitle, icon: Icon, color, fields }) => {
        const colors = colorMap[color];
        const enabled = settings[key].enabled;
        const expired = isExpired(settings[key].endDate);

        return (
          <div
            key={key}
            className={`rounded-2xl border ${enabled ? colors.border : "border-slate-200 dark:border-slate-700"} ${enabled ? colors.bg : "bg-white dark:bg-slate-800"} transition-all duration-300`}
          >
            {/* Section Header */}
            <div className="flex items-center justify-between p-5 border-b border-slate-100 dark:border-slate-700/50">
              <div className="flex items-center gap-3">
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${enabled ? colors.bg : "bg-slate-100 dark:bg-slate-700"}`}>
                  <Icon size={18} className={enabled ? colors.icon : "text-slate-400 dark:text-slate-500"} />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-bold text-sm text-slate-800 dark:text-white">{title}</h3>
                    {enabled && expired && (
                      <span className="flex items-center gap-1 text-[10px] font-bold text-red-500 bg-red-50 dark:bg-red-900/20 px-2 py-0.5 rounded-full">
                        <AlertCircle size={10} /> Abgelaufen
                      </span>
                    )}
                    {enabled && !expired && (
                      <span className="text-[10px] font-bold text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20 px-2 py-0.5 rounded-full">Aktiv</span>
                    )}
                  </div>
                  <p className="text-[11px] text-slate-500 dark:text-slate-400">{subtitle}</p>
                </div>
              </div>
              <button
                onClick={() => updateField(key, "enabled", !enabled)}
                className="transition-transform hover:scale-105"
              >
                {enabled ? (
                  <ToggleRight size={36} className={colors.icon} />
                ) : (
                  <ToggleLeft size={36} className="text-slate-300 dark:text-slate-600" />
                )}
              </button>
            </div>

            {/* Section Body */}
            {enabled && (
              <div className="p-5 space-y-4">
                {fields}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default AdminLaunchPromotions;
